import { useEffect, useState, memo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faBars,
  faPhone,
  faEnvelope,
  faChevronDown,
  faChevronUp,
  faUsers,
  faAward,
  faUserPlus,
  faHeart,
  faQuestionCircle,
  faLandmark,
  faUserTie,
  faUserFriends,
  faImages,
  faHandsHelping,
  faIdCard,
  faTrophy,
  faTimes,
  faNewspaper,
} from "@fortawesome/free-solid-svg-icons";

import {
  faFacebookF,
  faInstagram,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { GlobalData } from "../../assets/data/GlodalData";
import { CSDLogoAbout } from "../../assets/data/Imagedata";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Reusable DropdownMenu component (Adapted for CRC Bharat Colors)
const DropdownMenu = memo(
  ({ isOpen, links, imageSrc, imageAlt, onMouseEnter, onMouseLeave, closeAllMenus }) => {
    // Determine column layout based on number of links
    const numColumns = Math.min(
      links.length > 15 ? 5 : links.length > 10 ? 4 : links.length > 5 ? 3 : 2,
      5,
    );
    const linksPerColumn = Math.ceil(links.length / numColumns);
    const columns = Array.from({ length: numColumns }, (_, i) =>
      links.slice(i * linksPerColumn, (i + 1) * linksPerColumn),
    );

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="origin-top absolute top-full left-0 w-full rounded-b-xl divide-y divide-gray-200 z-50 shadow-xl bg-white/95 backdrop-blur-sm border-t border-gray-200"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <div className="flex w-full">
              {columns.map((columnLinks, colIndex) => (
                <div key={colIndex} className={`flex-1 p-6 border-r border-gray-100 last:border-r-0`}>
                  {columnLinks.map((link) => (
                    <Link to={link.path} key={link.path}>
                      <div className="block px-4 py-3 text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200 rounded-lg mb-1 group text-left">
                        <div className="flex items-start">
                          <FontAwesomeIcon
                            className="mt-1 mr-3 text-[18px] text-blue-600"
                            icon={link.icon}
                          />
                          <div>
                            <span className="font-semibold block mb-0.5 group-hover:text-blue-800">
                              {link.title}
                            </span>
                            <p className="text-[11px] text-gray-600 leading-tight">
                              {link.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ))}
              {imageSrc && (
                <div className="w-1/3 p-6 flex flex-col justify-center items-center bg-gray-50 rounded-r-lg">
                  <img
                    className="w-[150px] max-w-sm object-cover rounded-lg mb-4"
                    src={imageSrc}
                    alt={imageAlt || "Service Image"}
                  />
                  <div className="text-center">
                    <Link to="/aboutus" className="inline-block group">
                      <h2 className="text-gray-900 group-hover:text-blue-700 cursor-pointer font-bold text-[14px] flex items-center justify-center gap-2 transition-colors">
                        Learn More <span className="text-blue-600">➤</span>
                      </h2>
                    </Link>
                    <p className="text-[12px] text-gray-600 mt-1">
                      Discover more about us.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [isOpen, setIsOpen] = useState(false); // For mobile menu
  const [openMobileSubmenus, setOpenMobileSubmenus] = useState({});
  const closeTimeouts = useRef({}); // Per-menu timeouts using useRef
  const [isLargeScreen, setIsLargeScreen] = useState(false); // Detect lg screens

  const closeAllMenus = () => {
    setOpenMenus({});
  };

  const handleMouseEnter = (menuKey, hasLinks) => {
    if (!hasLinks) return;

    if (closeTimeouts.current[menuKey]) {
      clearTimeout(closeTimeouts.current[menuKey]);
      closeTimeouts.current[menuKey] = null;
    }
    setOpenMenus({ [menuKey]: true }); // Set only this menu to open
  };

  const handleMouseLeave = (menuKey, hasLinks) => {
    if (!hasLinks) return;

    closeTimeouts.current[menuKey] = setTimeout(() => {
      setOpenMenus((prev) => ({ ...prev, [menuKey]: false }));
    }, 200);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);

    // Prevent body scrolling when mobile menu is open
    if (isOpen && !isLargeScreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, isLargeScreen]);

  useEffect(() => {
    const checkScreenSize = () => {
      const largeScreen = window.innerWidth >= 1024; // lg breakpoint
      setIsLargeScreen(largeScreen);

      // Close mobile menu when switching to large screen
      if (largeScreen && isOpen) {
        setIsOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, [isOpen]);

  const NavImages = {
    about: CSDLogoAbout,
    milestones: CSDLogoAbout,
    committee: CSDLogoAbout,
    member: CSDLogoAbout,
    donate: CSDLogoAbout,
    faq: CSDLogoAbout,
  };

  const AboutLinks = [
    { path: "/aboutus", title: "About us", description: "Learn about our vision and mission.", icon: faBuilding },
    { path: "/gallery", title: "Gallery", description: "See our moments and activities.", icon: faImages },
    { path: "/news-highlights", title: "News Highlights", description: "See our News articals.", icon: faImages },
    { path: "/contactus", title: "Contact us", description: "Get in touch with our team.", icon: faPhone },
    { path: "/our-members", title: "Our Members", description: "Meet our registered members.", icon: faUserFriends },
  ];

  const OurMilestonesLinks = [
    { path: "/winning-moments", title: "Winning Moments", description: "Celebrating our achievements.", icon: faAward },
    { path: "/achievements", title: "Achievements", description: "Recognizing our progress.", icon: faTrophy },
    { path: "/gratitude", title: "Expression of Gratitude", description: "Thanking our supporters.", icon: faHandsHelping },
    { path: "/crc-publications", title: "CRC Publications", description: "Explore our official publications.", icon: faHandsHelping },
  ];

  const BecomeAMemberLinks = [
    { path: "/become-a-member", title: "Designated Member", description: "Lifetime membership – ₹2000", icon: faUserTie },
    { path: "/become-a-member", title: "Member", description: "1 year membership – ₹500", icon: faUserPlus },
    { path: "/become-a-member", title: "Youth Member / Volunteer", description: "2 years – ₹100", icon: faUsers },
    { path: "/id-card", title: "ID Cards", description: "Get your membership ID card.", icon: faIdCard },
  ];

  const menuConfigs = [
    { key: "About", title: "About", href: "/aboutus", links: AboutLinks, imageSrc: NavImages.about, icon: faBuilding },
    { key: "Education", title: "Education System", href: "/education-system", links: [], imageSrc: null, icon: faLandmark },
    { key: "VoiceOfConsumers", title: "Voice of Consumers", href: "/voice-of-consumers", links: [], imageSrc: null, icon: faNewspaper },
    { key: "Milestones", title: "Our Milestones", href: "#", links: OurMilestonesLinks, imageSrc: NavImages.milestones, icon: faAward },
    { key: "Committee", title: "Committee", href: "/our-committee", links: [], imageSrc: NavImages.committee, icon: faUsers },
    { key: "Member", title: "Become a Member", href: "/become-a-member", links: BecomeAMemberLinks, imageSrc: NavImages.member, icon: faUserPlus },
    { key: "Donate", title: "Donate", href: "/donate", links: [], imageSrc: NavImages.donate, icon: faHeart },
    { key: "knowledgehub", title: "Knowledge Hub", href: "/frequently-asked-questions", links: [], imageSrc: NavImages.faq, icon: faQuestionCircle },
  ];

  const toggleMobileSubmenu = (key) => {
    setOpenMobileSubmenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setOpenMobileSubmenus({});
  };

  return (
    <section
      className={`origin-top-right sticky w-full top-0 z-50 transition-colors duration-500 shadow-lg ${
        scrolled ? "bg-white/95 backdrop-blur-sm" : "bg-white"
      }`}
    >
      <div className="overflow-hidden bg-gradient-to-r from-blue-900 to-blue-800 text-white text-sm px-4 py-2">
        <div className=" mx-auto flex flex-row items-center justify-between w-full">
          <a
            href={`tel:${GlobalData.company.companyPhone}`}
            className="text-[12px] sm:text-[14px] flex items-center gap-2 hover:text-blue-300 transition-colors shrink-0"
          >
            <FontAwesomeIcon className="text-[12px]" icon={faPhone} />
            <span className="text-[12px] sm:text-[14px] hidden sm:block">
              {GlobalData.company.companyPhone}
            </span>
          </a>
          <a
            href={`mailto:${GlobalData.company.companyEmail}`}
            className="flex items-center gap-2 hover:text-blue-300 transition-colors hidden sm:flex shrink-0 ml-4"
          >
            <FontAwesomeIcon className="text-[14px]" icon={faEnvelope} />
            <span className="text-[12px] sm:text-[14px] hidden sm:block">
              {GlobalData.company.companyEmail}
            </span>
          </a>
          {/* Scrolling Text */}
          <div className="relative flex-1 overflow-hidden min-h-[20px] mx-4">
            <div className="animate-scroll whitespace-nowrap text-[12px] sm:text-[14px]">
               Fight for your right! We are a dedicated organization committed to protecting and empowering consumers across India.
            </div>
          </div>

          <div className="flex items-center space-x-4 shrink-0">
            <div className="flex space-x-3">
              <a
                href={GlobalData.company.companyfacebook}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-300 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faFacebookF} className="text-[14px]" />
              </a>
              <a
                href={GlobalData.company.companyinstagram}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-pink-300 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faInstagram} className="text-[14px]" />
              </a>
              <a
                href={GlobalData.company.companytwitter}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sky-300 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faTwitter} className="text-[14px]" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <div className=" mx-auto px-4 sm:px-6 w-full h-16 flex justify-between items-center text-gray-700 relative">
        <Link to="/" className="z-50">
          <img
            className="p-2 h-[80px] sm:h-24 w-auto transition-transform hover:scale-105 duration-200"
            src={CSDLogoAbout}
            alt="csd_logo"
          />
        </Link>

        <div className="justify-end h-full flex">
          <ul className="flex items-stretch h-full">
            {/* Dynamic desktop menu items */}
            {menuConfigs.map((menu) => (
                <li
                  key={menu.key}
                  className="inline-flex h-full items-center text-left hidden lg:flex"
                  onMouseEnter={() =>
                    handleMouseEnter(menu.key, menu.links.length > 0)
                  }
                  onMouseLeave={() =>
                    handleMouseLeave(menu.key, menu.links.length > 0)
                  }
                >
                  <Link
                    to={menu.href}
                    className={classNames(
                      "inline-flex flex-col font-normal antialiased justify-center items-center gap-0 rounded-md px-3 py-1 mx-1 text-[11px] cursor-pointer transition-colors group",
                      openMenus[menu.key] ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                    )}
                  >
                    <FontAwesomeIcon
                      icon={menu.icon}
                      className="mb-1 text-[18px] group-hover:scale-110 transition-transform duration-200"
                    />
                    <div className="flex items-center gap-1 text-center">
                      <span>{menu.title}</span>

                      {/* Toggle Chevron Up/Down */}
                      {menu.links.length > 0 && (
                        <FontAwesomeIcon
                          icon={
                            openMenus[menu.key] ? faChevronUp : faChevronDown
                          }
                          className="text-[8px] transition-transform duration-200"
                        />
                      )}
                    </div>
                  </Link>

                  <DropdownMenu
                    isOpen={openMenus[menu.key]}
                    links={menu.links}
                    imageSrc={menu.imageSrc}
                    imageAlt={menu.title}
                    onMouseEnter={() =>
                      handleMouseEnter(menu.key, menu.links.length > 0)
                    }
                    onMouseLeave={() =>
                      handleMouseLeave(menu.key, menu.links.length > 0)
                    }
                    closeAllMenus={closeAllMenus}
                  />
                </li>
              ))}

            {/* Hamburger Button */}
            <li className="p-2 z-50 relative lg:hidden">
              <button
                className="text-gray-700 hover:text-blue-600 transition-colors p-2"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle menu"
              >
                <FontAwesomeIcon
                  icon={isOpen ? faTimes : faBars}
                  className="text-[24px] transition-transform duration-300"
                />
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div
        className={`fixed inset-0 bg-white z-[60] flex flex-col transition-transform duration-500 ease-in-out lg:hidden h-screen ${
          isOpen ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        {/* Mobile Menu Header (Sticky) */}
        <div className="flex-none flex justify-between items-center p-4 border-b border-gray-200 bg-white/95 backdrop-blur-md">
          <Link to="/" onClick={handleLinkClick}>
            <img className="h-10 w-auto" src={CSDLogoAbout} alt="csd_logo" />
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-700 p-2 rounded-full hover:bg-gray-100 hover:text-blue-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-[24px]" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 pb-20">
          <div className="flex flex-col">
            {menuConfigs.map((menu, i) => (
              <div key={i} className="border-b border-gray-100 last:border-b-0">
                {menu.links.length > 0 ? (
                  <>
                    <button
                      onClick={() => toggleMobileSubmenu(menu.key)}
                      className="w-full flex justify-between items-center py-4 text-left text-[18px] font-medium text-gray-800 hover:text-blue-600 transition-colors group"
                    >
                      <div className="flex items-center gap-4">
                        <span className="w-8 flex justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
                          <FontAwesomeIcon icon={menu.icon} />
                        </span>
                        <span>{menu.title}</span>
                      </div>
                      <FontAwesomeIcon
                        icon={
                          openMobileSubmenus[menu.key]
                            ? faChevronUp
                            : faChevronDown
                        }
                        className={`text-[14px] text-gray-400 transition-transform duration-300 ${
                          openMobileSubmenus[menu.key] ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        openMobileSubmenus[menu.key]
                          ? "max-h-[1000px] opacity-100 mb-4"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="ml-10 grid grid-cols-1 gap-1 border-l-2 border-blue-200 pl-4">
                        {menu.links.map((sub, idx) => (
                          <Link
                            key={idx}
                            to={sub.path}
                            onClick={handleLinkClick}
                            className="flex items-start gap-3 py-3 text-[14px] text-gray-600 hover:text-blue-700 hover:translate-x-1 transition-all group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 group-hover:text-blue-700 group-hover:bg-blue-100 transition-all shrink-0">
                              <FontAwesomeIcon
                                icon={sub.icon}
                                className="text-[12px]"
                              />
                            </div>
                            <div className="flex flex-col">
                              <span className="block font-medium text-[13px]">
                                {sub.title}
                              </span>
                              {sub.description && (
                                <span className="text-[10px] text-gray-500 line-clamp-1 leading-tight mt-0.5">
                                  {sub.description}
                                </span>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={menu.href}
                    onClick={handleLinkClick}
                    className="w-full flex items-center gap-4 py-4 text-[18px] font-medium text-gray-800 hover:text-blue-600 transition-colors group"
                  >
                    <span className="w-8 flex justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
                      <FontAwesomeIcon icon={menu.icon} />
                    </span>
                    <span>{menu.title}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Contact Footer */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-blue-600 font-semibold mb-4 uppercase text-[14px] tracking-wider">
              Contact Us
            </h3>
            <div className="space-y-4">
              <a
                href={`tel:${GlobalData.company.companyPhone}`}
                className="flex items-center gap-3 text-gray-600 hover:text-blue-700 transition-colors"
                onClick={handleLinkClick}
              >
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                  <FontAwesomeIcon icon={faPhone} size="sm" />
                </div>
                <span>{GlobalData.company.companyPhone}</span>
              </a>
              <a
                href={`mailto:${GlobalData.company.companyEmail}`}
                className="flex items-center gap-3 text-gray-600 hover:text-blue-700 transition-colors"
                onClick={handleLinkClick}
              >
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                  <FontAwesomeIcon icon={faEnvelope} size="sm" />
                </div>
                <span>{GlobalData.company.companyEmail}</span>
              </a>
            </div>

            <div className="flex gap-4 mt-6 justify-center text-center">
              <a
                href={GlobalData.company.companyfacebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-blue-600 hover:text-white transition-all"
                onClick={handleLinkClick}
              >
                <FontAwesomeIcon icon={faFacebookF} />
              </a>
              <a
                href={GlobalData.company.companyinstagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-pink-600 hover:text-white transition-all"
                onClick={handleLinkClick}
              >
                <FontAwesomeIcon icon={faInstagram} />
              </a>
              <a
                href={GlobalData.company.companytwitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-sky-500 hover:text-white transition-all"
                onClick={handleLinkClick}
              >
                <FontAwesomeIcon icon={faTwitter} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Navbar;
